<template>
  <div class="dashboard-container d-flex">
    <!-- Sidebar -->
    <EmployeeSidebar :sidebarOpen="sidebarOpen" currentPage="dashboard" />

    <!-- Main Content -->
    <div
      class="main-content flex-grow-1"
      :class="{ 'sidebar-collapsed': !sidebarOpen }"
    >
      <!-- Navbar -->
      <nav class="navbar navbar-light bg-white border-bottom px-4">
        <div class="d-flex w-100 justify-content-between align-items-center">
          <div class="d-flex align-items-center gap-3">
            <button class="btn btn-light" @click="toggleSidebar">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                class="bi bi-list"
                viewBox="0 0 16 16"
              >
                <path
                  fill-rule="evenodd"
                  d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"
                />
              </svg>
            </button>
            <div>
              <h2 class="mb-0 fw-bold">{{ $t('employee.dashboard.title') }}</h2>
              <small class="text-muted" v-if="user"
                >{{ $t('employee.dashboard.welcomeBack') }}, {{ user.name }}!</small
              >
            </div>
          </div>
          <button class="btn btn-outline-danger btn-sm" @click="handleLogout">
            {{ $t('common.logout') }}
          </button>
        </div>
      </nav>

      <!-- Content -->
      <div class="container-fluid p-4">
        <!-- Statistics Cards -->
        <div class="row mb-4">
          <div class="col-md-6 col-lg-3 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-info">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('employee.dashboard.myRole') }}</p>
                    <h5 class="mb-0 fw-bold">{{ employeeInfo.role }}</h5>
                    <small class="text-muted">{{ employeeInfo.status }}</small>
                  </div>
                  <div class="stat-icon bg-info">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-person-badge"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M6.5 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3zM11 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"
                      />
                      <path
                        d="M4.5 0A2.5 2.5 0 0 0 2 2.5V14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2.5A2.5 2.5 0 0 0 11.5 0h-7zM3 2.5A1.5 1.5 0 0 1 4.5 1h7A1.5 1.5 0 0 1 13 2.5v10.795a4.2 4.2 0 0 0-.776-.492C11.392 12.387 10.063 12 8 12s-3.392.387-4.224.803a4.2 4.2 0 0 0-.776.492V2.5z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-lg-3 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-success">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">Hours This Month</p>
                    <h3 class="mb-0 fw-bold">{{ monthlyHours }}h</h3>
                    <small class="text-muted">{{
                      formatCurrency(expectedEarnings)
                    }}</small>
                  </div>
                  <div class="stat-icon bg-success">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-clock-history"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 0 1 .589.022l-.074.997zm2.004.45a7.003 7.003 0 0 0-.985-.299l.219-.976c.383.086.76.2 1.126.342l-.36.933zm1.37.71a7.01 7.01 0 0 0-.439-.27l.493-.87a8.025 8.025 0 0 1 .979.654l-.615.789a6.996 6.996 0 0 0-.418-.302zm1.834 1.79a6.99 6.99 0 0 0-.653-.796l.724-.69c.27.285.52.59.747.91l-.818.576zm.744 1.352a7.08 7.08 0 0 0-.214-.468l.893-.45a7.976 7.976 0 0 1 .45 1.088l-.95.313a7.023 7.023 0 0 0-.179-.483zm.53 2.507a6.991 6.991 0 0 0-.1-1.025l.985-.17c.067.386.106.778.116 1.17l-1 .025zm-.131 1.538c.033-.17.06-.339.081-.51l.993.123a7.957 7.957 0 0 1-.23 1.155l-.964-.267c.046-.165.086-.332.12-.501zm-.952 2.379c.184-.29.346-.594.486-.908l.914.405c-.16.36-.345.706-.555 1.038l-.845-.535zm-.964 1.205c.122-.122.239-.248.35-.378l.758.653a8.073 8.073 0 0 1-.401.432l-.707-.707z"
                      />
                      <path
                        d="M8 1a7 7 0 1 0 4.95 11.95l.707.707A8.001 8.001 0 1 1 8 0v1z"
                      />
                      <path
                        d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-lg-3 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-warning">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('employee.dashboard.pendingTimesheets') }}</p>
                    <h3 class="mb-0 fw-bold">{{ pendingTimesheets }}</h3>
                    <small class="text-muted">{{ $t('employee.dashboard.awaitingApproval') }}</small>
                  </div>
                  <div class="stat-icon bg-warning">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-hourglass-split"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M2.5 15a.5.5 0 1 1 0-1h1v-1a4.5 4.5 0 0 1 2.557-4.06c.29-.139.443-.377.443-.59v-.7c0-.213-.154-.451-.443-.59A4.5 4.5 0 0 1 3.5 3V2h-1a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-1v1a4.5 4.5 0 0 1-2.557 4.06c-.29.139-.443.377-.443.59v.7c0 .213.154.451.443.59A4.5 4.5 0 0 1 12.5 13v1h1a.5.5 0 0 1 0 1h-11zm2-13v1c0 .537.12 1.045.337 1.5h6.326c.216-.455.337-.963.337-1.5V2h-7zm3 6.35c0 .701-.478 1.236-1.011 1.492A3.5 3.5 0 0 0 4.5 13s.866-1.299 3-1.48V8.35zm1 0v3.17c2.134.181 3 1.48 3 1.48a3.5 3.5 0 0 0-1.989-3.158C8.978 9.586 8.5 9.052 8.5 8.351z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-lg-3 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-danger">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">Outstanding Advances</p>
                    <h3 class="mb-0 fw-bold">
                      {{ formatCurrency(outstandingAdvances) }}
                    </h3>
                    <small class="text-muted">To be recovered</small>
                  </div>
                  <div class="stat-icon bg-danger">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-exclamation-triangle"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"
                      />
                      <path
                        d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="card border-0 shadow-sm">
              <div class="card-body">
                <h5 class="card-title mb-3">{{ $t('employee.dashboard.quickActions') }}</h5>
                <div class="row g-3">
                  <div class="col-md-3">
                    <router-link
                      to="/employee/timesheets"
                      class="btn btn-primary btn-lg w-100"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        class="bi bi-plus-circle me-2"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
                        />
                        <path
                          d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"
                        />
                      </svg>
                      {{ $t('employee.timesheets.logTodaysHours') }}
                    </router-link>
                  </div>
                  <div class="col-md-3">
                    <router-link
                      to="/employee/advances"
                      class="btn btn-warning btn-lg w-100"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        class="bi bi-wallet2 me-2"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M12.136.326A1.5 1.5 0 0 1 14 1.78V3h.5A1.5 1.5 0 0 1 16 4.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 13.5v-9a1.5 1.5 0 0 1 1.432-1.499L12.136.326zM5.562 3H13V1.78a.5.5 0 0 0-.621-.484L5.562 3zM1.5 4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-13z"
                        />
                      </svg>
                      {{ $t('employee.dashboard.requestAdvance') }}
                    </router-link>
                  </div>
                  <div class="col-md-3">
                    <router-link
                      to="/employee/payslips"
                      class="btn btn-success btn-lg w-100"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        class="bi bi-file-earmark-text me-2"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z"
                        />
                        <path
                          d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"
                        />
                      </svg>
                      {{ $t('employee.dashboard.viewPayslips') }}
                    </router-link>
                  </div>
                  <div class="col-md-3">
                    <router-link
                      to="/employee/profile"
                      class="btn btn-info btn-lg w-100"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        class="bi bi-person-fill me-2"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
                        />
                      </svg>
                      {{ $t('employee.dashboard.myProfile') }}
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Employment Info -->
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <h5 class="card-title mb-3">{{ $t('employee.dashboard.employmentInformation') }}</h5>
                <table class="table table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td class="text-muted">{{ $t('employee.dashboard.name') }}:</td>
                      <td class="fw-semibold">{{ user ? user.name : "" }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">{{ $t('employee.dashboard.role') }}:</td>
                      <td class="fw-semibold">{{ employeeInfo.role }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">{{ $t('employee.dashboard.payRate') }}:</td>
                      <td class="fw-semibold">
                        {{ formatCurrency(employeeInfo.payRate) }}/hour
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">{{ $t('employee.dashboard.contractValidTill') }}:</td>
                      <td class="fw-semibold">
                        {{ employeeInfo.contractValidTill }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">{{ $t('employee.dashboard.status') }}:</td>
                      <td>
                        <span
                          class="badge"
                          :class="
                            employeeInfo.status === 'Active'
                              ? 'bg-success'
                              : 'bg-danger'
                          "
                        >
                          {{ employeeInfo.status }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <h5 class="card-title mb-3">{{ $t('employee.dashboard.thisMonthSummary') }}</h5>
                <div class="mb-3 pb-3 border-bottom">
                  <div class="d-flex justify-content-between">
                    <span class="text-muted">{{ $t('employee.dashboard.submittedTimesheets') }}:</span>
                    <span class="fw-bold">{{ submittedTimesheets }}</span>
                  </div>
                </div>
                <div class="mb-3 pb-3 border-bottom">
                  <div class="d-flex justify-content-between">
                    <span class="text-muted">{{ $t('employee.dashboard.approvedHours') }}:</span>
                    <span class="fw-bold text-success"
                      >{{ approvedHours }}h</span
                    >
                  </div>
                </div>
                <div class="mb-3 pb-3 border-bottom">
                  <div class="d-flex justify-content-between">
                    <span class="text-muted">{{ $t('employee.dashboard.expectedEarnings') }}:</span>
                    <span class="fw-bold text-success">{{
                      formatCurrency(expectedEarnings)
                    }}</span>
                  </div>
                </div>
                <div>
                  <div class="d-flex justify-content-between">
                    <span class="text-muted">{{ $t('employee.dashboard.advancesToRecover') }}:</span>
                    <span class="fw-bold text-danger"
                      >-{{ formatCurrency(outstandingAdvances) }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Contract Document -->
        <div class="row">
          <div class="col-12 mb-4">
            <ContractDocument />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EmployeeSidebar from "@/components/EmployeeSidebar.vue";
import ContractDocument from "@/components/ContractDocument.vue";
const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

export default {
  name: "EmployeeDashboard",
  components: {
    EmployeeSidebar,
    ContractDocument
  },
  data() {
    return {
      sidebarOpen: true,
      user: {
        name: "",
        email: "",
        role: "employee",
      },
      loading: false,
      employeeInfo: {
        name: "",
        role: "",
        payRate: 0,
        contractValidTill: "",
        status: "Active",
      },
      timesheets: [],
      advances: [],
    };
  },
  computed: {
    // Days are the unit of entry, so these counts read off days[] not weeks
    allDays() {
      return this.timesheets.flatMap((t) => t.days || []);
    },
    daysThisMonth() {
      const thisMonth = new Date().getMonth();
      const thisYear = new Date().getFullYear();
      return this.allDays.filter((day) => {
        const date = new Date(day.work_date);
        return (
          day.status !== "draft" &&
          date.getMonth() === thisMonth &&
          date.getFullYear() === thisYear
        );
      });
    },
    monthlyHours() {
      return this.daysThisMonth.reduce(
        (sum, day) => sum + parseFloat(day.hours || 0),
        0
      );
    },
    pendingTimesheets() {
      return this.allDays.filter((day) => day.status === "pending").length;
    },
    submittedTimesheets() {
      return this.daysThisMonth.length;
    },
    approvedHours() {
      return this.daysThisMonth
        .filter((day) => ["approved", "paid"].includes(day.status))
        .reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
    },
    expectedEarnings() {
      return this.approvedHours * (this.employeeInfo.payRate || 0);
    },
    outstandingAdvances() {
      return this.advances
        .filter((a) => a.status === "approved" && !a.paid_at)
        .reduce((sum, a) => sum + parseFloat(a.amount || 0), 0);
    },
  },
  mounted() {
    const userData = localStorage.getItem("user");
    if (!userData) {
      this.$router.push("/");
      return;
    }

    this.user = JSON.parse(userData);

    // Redirect admin users
    if (this.user.role === "admin") {
      this.$router.push("/dashboard");
      return;
    }

    this.loadDashboardData();
  },
  methods: {
    async loadDashboardData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadEmployeeProfile(),
          this.loadTimesheets(),
          this.loadAdvances(),
        ]);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        this.loading = false;
      }
    },
    async loadEmployeeProfile() {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/api/employees/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch employee profile");
        const data = await response.json();
        this.employeeInfo = {
          name: data.name || "",
          role: data.role || "",
          payRate: parseFloat(data.pay_rate) || 0,
          contractValidTill: data.contract_valid_till
            ? new Date(data.contract_valid_till).toLocaleDateString("de-DE")
            : "N/A",
          status: data.status === "active" ? "Active" : "Inactive",
        };
      } catch (error) {
        console.error("Error loading employee profile:", error);
      }
    },
    async loadTimesheets() {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_URL}/api/timesheets?limit=1000`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch timesheets");
        const result = await response.json();
        this.timesheets =
          result.success && Array.isArray(result.data) ? result.data : [];
      } catch (error) {
        console.error("Error loading timesheets:", error);
        this.timesheets = [];
      }
    },
    async loadAdvances() {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_URL}/api/advances/employee`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch advances");
        const result = await response.json();
        this.advances =
          result.success && Array.isArray(result.data) ? result.data : [];
      } catch (error) {
        console.error("Error loading advances:", error);
        this.advances = [];
      }
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount);
    },
    handleLogout() {
      localStorage.removeItem("user");
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.sidebar {
  width: 280px;
  background-color: #2c3e50;
  color: white;
  min-height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1000;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: padding 0.3s ease;
}

.sidebar.collapsed .sidebar-header {
  padding: 2rem 0.5rem;
}

.sidebar-header h4 {
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-nav {
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.05rem;
  gap: 1rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar.collapsed .nav-item {
  padding: 1rem 0.5rem;
  justify-content: center;
  font-size: 0;
}

.sidebar.collapsed .nav-item svg {
  margin: 0;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-left: 4px solid #667eea;
}

.nav-item svg {
  flex-shrink: 0;
}

.main-content {
  margin-left: 280px;
  min-height: 100vh;
  background-color: #f8f9fa;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.navbar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-card {
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.sidebar-language-switcher {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
}

@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0 !important;
  }
}
</style>
